import type { MessageDescriptor } from "@lingui/core";

import { msg, plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import {
  apiGetAnalyticsAlerts,
  apiGetAnalyticsAudit,
  apiGetAnalyticsComputed,
  apiGetAnalyticsEvents,
  apiGetAnalyticsPpg,
  ListCounts,
  ListCountsWithPatients,
} from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { Button } from "../../../components/ui-kit/button";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import { Analytics, AnalyticsItem } from "./analytics";

export const CountsAnalytics = observer(function CountsAnalytics(props: {
  type: string;
  api: ApiConnector;
}) {
  const { t } = useLingui();
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [freq, setFreq] = useState("1D");
  const [counts, setCounts] = useState<Analytics | null>(null);

  // Results belong to the previously selected count type; the date range and
  // grouping deliberately survive a type switch.
  useEffect(() => {
    setCounts(null);
  }, [props.type]);

  return (
    <div>
      <div>
        <label>
          <Trans>From:</Trans>{" "}
          <DateInput
            type="date"
            value={dateFrom}
            onChange={(newValue) => {
              setDateFrom(newValue);
              setCounts(null);
            }}
          />
        </label>{" "}
        <label>
          <Trans>To:</Trans>{" "}
          <DateInput
            type="date"
            value={dateTo}
            onChange={(newValue) => {
              setDateTo(newValue);
              setCounts(null);
            }}
          />
        </label>
        <br className="h-5" />
        <label>
          <Trans>Temporal grouping:</Trans>{" "}
          <Select
            values={groupingFreq}
            value={freq}
            onChange={(newValue) => {
              setFreq(newValue);
              setCounts(null);
            }}
          />
        </label>
        <br className="h-5" />
        <Button
          variant="primary"
          onClick={async () => {
            const counts = await loadAnalytics(
              props.type,
              dateFrom,
              dateTo,
              freq,
              props.api,
            );
            setCounts(counts);
          }}
        >
          <Trans>Load numbers</Trans>
        </Button>
      </div>

      {counts != null && (
        <>
          <br className="h-10" />
          <DataTable
            persistState
            model={counts}
            itemKeyField="key"
            filterNoun={(count) =>
              t`${plural(count, { one: "row", other: "rows" })}`
            }
            columns={[
              {
                header: <Trans>Date/time</Trans>,
                attr: "timestamp",
                type: "date",
                sort: true,
                initSort: "desc",
              },
              {
                header: <Trans>Type</Trans>,
                attr: "data_type",
                type: "string",
                filter: {
                  kind: "multi",
                  group: t`Analytics`,
                  options: uniqueValueOptions<AnalyticsItem>("data_type"),
                },
              },
              {
                header: <Trans>Total counts</Trans>,
                attr: "data_count",
                type: "number",
              },
              {
                header: <Trans># unique patients</Trans>,
                attr: "unique_patients",
                type: "number",
              },
            ]}
          />
        </>
      )}
    </div>
  );
});

const groupingFreq: Record<string, MessageDescriptor> = {
  "1D": msg`daily`,
  "1W": msg`weekly`,
  "2W": msg`bi-weekly`,
  "1ME": msg`monthly`,
};

async function loadAnalytics(
  type: string,
  fromDate: string | null,
  toDate: string | null,
  freq: string,
  api: ApiConnector,
) {
  const model = new Analytics([], api.permissions, api.requireUserUUID());
  let counts: ListCountsWithPatients | ListCounts | null = null;
  let dateFromStr: string | null = null;
  let dateToStr: string | null = null;
  if (fromDate === null) {
    dateFromStr = null;
  } else {
    const dateFrom = new Date(fromDate);
    dateFrom.setHours(0, 0, 0, 0);
    dateFromStr = dateFrom.toISOString();
  }
  if (toDate === null) {
    dateToStr = null;
  } else {
    const dateTo = new Date(toDate);
    dateTo.setHours(23, 59, 59, 0);
    dateToStr = dateTo.toISOString();
  }

  if (type === "computed") {
    counts = (
      await apiGetAnalyticsComputed({
        query: {
          datetime_from: dateFromStr,
          datetime_to: dateToStr,
          grouping_frequency: freq,
        },
      })
    ).data;
  } else if (type === "ppg") {
    counts = (
      await apiGetAnalyticsPpg({
        query: {
          datetime_from: dateFromStr,
          datetime_to: dateToStr,
          grouping_frequency: freq,
        },
      })
    ).data;
  } else if (type === "events") {
    counts =
      (
        await apiGetAnalyticsEvents({
          query: {
            datetime_from: dateFromStr,
            datetime_to: dateToStr,
            grouping_frequency: freq,
          },
          throwOnError: false,
        })
      ).data ?? null;
  } else if (type === "audit") {
    counts = (
      await apiGetAnalyticsAudit({
        query: {
          datetime_from: dateFromStr,
          datetime_to: dateToStr,
          grouping_frequency: freq,
        },
      })
    ).data;
  } else if (type === "alerts") {
    counts = (
      await apiGetAnalyticsAlerts({
        query: {
          datetime_from: dateFromStr,
          datetime_to: dateToStr,
          grouping_frequency: freq,
        },
      })
    ).data;
  }

  if (counts) {
    model.data = counts.counts;
    model.init();
  }

  return model;
}
